import { useState } from "react";
import { api, ApiError, type ListerEnrichRow, type ListerExisting } from "../../../lib/api";
import { fmtCents } from "../../../lib/format";
import EnrichPanel from "./EnrichPanel";

export default function DuplicateModal({ sessionId, existing, initial, onRestock, onCreateAnyway, onClose, result }: {
  sessionId: string;
  existing: ListerExisting; initial: number;
  onRestock: (delta: number, mode: "add" | "replace") => void;
  onCreateAnyway: () => void;
  onClose: () => void;
  result: { available: number | null; mode: "add" | "replace" } | null;
}) {
  const [n, setN] = useState(initial);
  const [mode, setMode] = useState<"add" | "replace">("add");
  const [armed, setArmed] = useState(false);
  // The synthetic recently-posted duplicate carries available: null (routes/lister.ts) — the very
  // path where a wrong replace is likeliest, because the product is minutes old. Say so in words:
  // "the current count of null" is worse than admitting the number is not known here.
  const currentCount = existing.available === null
    ? `This sets the stock level to ${n} and discards whatever it is now — this scan cannot see the current count.`
    : `This discards the current count of ${existing.available} and sets the stock level to ${n}.`;

  // Enriching this product, in place of the action row. `rows` is null before the fetch
  // resolves and [] when this scan has no proposal yet (research never ran) — EnrichPanel
  // itself renders that second case plainly and with no Apply button.
  const [enriching, setEnriching] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichRows, setEnrichRows] = useState<ListerEnrichRow[]>([]);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [enrichApply, setEnrichApply] = useState<Record<string, boolean>>({});
  const [appendImages, setAppendImages] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichResult, setEnrichResult] = useState<{ applied: string[]; warnings: string[]; admin_url: string } | null>(null);
  const [enrichFailure, setEnrichFailure] = useState<{ step: string; applied: string[] } | null>(null);

  async function startEnrich(): Promise<void> {
    setEnriching(true);
    setEnrichLoading(true);
    setEnrichError(null);
    try {
      const r = await api.getListerExistingDetail(sessionId);
      setEnrichRows(r.rows);
    } catch (e) {
      setEnrichError(String(e));
    } finally {
      setEnrichLoading(false);
    }
  }

  function toggleEnrich(key: string): void {
    setEnrichApply((prev) => {
      const row = enrichRows.find((r) => r.key === key);
      const current = prev[key] ?? row?.auto ?? false;
      return { ...prev, [key]: !current };
    });
  }

  async function applyEnrich(): Promise<void> {
    setEnrichBusy(true);
    setEnrichFailure(null);
    try {
      const r = await api.enrichListerSession(sessionId, { apply: enrichApply, images: appendImages ? "append" : "skip" });
      setEnrichResult(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 502) {
        const body = e.body as { step: string; applied: string[] };
        setEnrichFailure({ step: body.step, applied: body.applied });
      } else {
        setEnrichError(String(e));
      }
    } finally {
      setEnrichBusy(false);
    }
  }

  const done = result !== null || enrichResult !== null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-end md:items-center justify-center z-50" role="dialog" aria-label="Already in store">
      <div className="w-full md:max-w-md bg-zinc-900 border border-zinc-800 rounded-t-md md:rounded-md p-4 space-y-3 font-mono text-sm">
        <h2 className="text-zinc-100 text-base">Already in store</h2>
        <p className="text-zinc-300">{existing.title}</p>
        <p className="text-zinc-500">{fmtCents(existing.price_cents)} · {existing.available ?? "?"} in stock · <a className="underline" href={existing.admin_url} target="_blank" rel="noopener">open in admin</a></p>
        {result !== null ? (
          <p className="text-green-300">{result.mode === "replace" ? "Replaced" : "Added"} — now {result.available ?? "?"} in stock.</p>
        ) : enriching ? (
          <>
            {enrichError && <p className="text-red-400 text-xs" role="alert">{enrichError}</p>}
            {enrichLoading ? (
              <p className="text-zinc-500 text-xs">loading the product Shopify actually holds…</p>
            ) : (
              <EnrichPanel
                rows={enrichRows}
                apply={enrichApply}
                onToggle={toggleEnrich}
                appendImages={appendImages}
                onAppendImages={setAppendImages}
                busy={enrichBusy}
                onApply={() => void applyEnrich()}
                onCancel={() => setEnriching(false)}
                result={enrichResult}
                failure={enrichFailure}
              />
            )}
          </>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input aria-label="quantity" type="number" inputMode="numeric" min={1} value={n}
                onChange={(e) => { setN(Math.max(1, Number(e.target.value))); setArmed(false); }}
                className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-3 text-center" />
              <div className="flex rounded overflow-hidden border border-zinc-800" role="group" aria-label="restock mode">
                <button type="button" aria-pressed={mode === "add"} className={`px-3 py-3 ${mode === "add" ? "bg-zinc-700 text-zinc-100" : "bg-zinc-900 text-zinc-400"}`}
                  onClick={() => { setMode("add"); setArmed(false); }}>Add</button>
                <button type="button" aria-pressed={mode === "replace"} className={`px-3 py-3 ${mode === "replace" ? "bg-zinc-700 text-zinc-100" : "bg-zinc-900 text-zinc-400"}`}
                  onClick={() => { setMode("replace"); setArmed(false); }}>Replace</button>
              </div>
              <button
                className="bg-zinc-800 hover:bg-zinc-700 px-4 py-3 rounded min-w-[44px]"
                onClick={() => {
                  if (mode === "add") { onRestock(n, "add"); return; }
                  if (!armed) { setArmed(true); return; }
                  onRestock(n, "replace");
                }}
              >{mode === "add" ? `Add ${n}` : armed ? `Replace with ${n}` : `Replace with ${n}…`}</button>
            </div>
            {mode === "replace" && armed && <p className="text-amber-300">{currentCount} Press again to replace.</p>}
            <div className="flex items-center gap-3">
              <button className="text-zinc-400 underline" onClick={() => void startEnrich()}>Enrich this product</button>
              <button className="ml-auto text-zinc-400 underline" onClick={onCreateAnyway}>Create anyway</button>
            </div>
          </div>
        )}
        <button className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-3 rounded" onClick={onClose}>{done ? "Scan next" : "Cancel"}</button>
      </div>
    </div>
  );
}
